import React, { useState, useEffect, useContext, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import DriverContext from "../context/DriverContext";
import "../pages/FleetLayout.css";
import { useTranslation } from "react-i18next";
import formatTimestamp from "../utils/timestampConvert";
import { Modal, Typography } from "antd";
import { MessageOutlined, TeamOutlined } from "@ant-design/icons";
import LayoutFilter from "../components/layoutFilter/LayoutFilter";
import ContextToolbar from "../components/contextToolbar/ContextToolbar";
import BroadcastComponent from "../components/broadcast/Broadcast";
import {
  MdLocalShipping,
  MdOutlineLocalShipping,
  MdOutlinePersonOff,
  MdSpeed,
  MdUpdate,
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
  MdMap,
  MdTerrain,
  MdSatellite,
} from "react-icons/md";
import { useGlobalSettings } from "../context/GlobalSettingsContext";
import { subscribeFleetDetails } from "../services/fleetDetailsFirestore";
import { buildDriverMarkerStyleSet, createDriverMarkerStyles } from "../utils/driverMapMarkers";
import OlMap from "ol/Map";
import View from "ol/View";
import Overlay from "ol/Overlay";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import XYZ from "ol/source/XYZ";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import { Fill, Stroke, Style, RegularShape } from "ol/style";
import { fromLonLat, toLonLat } from "ol/proj";
import { unByKey } from "ol/Observable";
import "ol/ol.css";
import { createFleetLocationMarkerStyle } from "../utils/fleetLocationMarker";

const centerMadrid = { lat: 40.4168, lng: -3.7038 };
const BLANK_TILE_DATA_URI = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==";
const HERE_TILES_2D_URL_TEMPLATE = "https://maps.hereapi.com/v3/base/mc/{z}/{x}/{y}/png8";
const HERE_ATTRIBUTION = '&copy; <a href="https://www.here.com">HERE</a>';
const HERE_LANGUAGE_BY_LOCALE = {
  en: "en",
  es: "es",
  ca: "ca",
  de: "de",
  fr: "fr",
  ru: "ru",
  pl: "pl",
};
const HERE_BASEMAP_OPTIONS = [
  {
    id: "lite-auto",
    labelKey: "fleetLayout.mapModes.lite",
    hintKey: "fleetLayout.mapModes.autoDayNight",
    icon: MdMap,
    baseStyle: "lite",
    autoDayNight: true,
    imageFormat: "png8",
    features: "pois:disabled",
    supportsLanguage: true,
  },
  {
    id: "topo-auto",
    labelKey: "fleetLayout.mapModes.topographic",
    hintKey: "fleetLayout.mapModes.autoDayNight",
    icon: MdTerrain,
    baseStyle: "topo",
    autoDayNight: true,
    imageFormat: "png8",
    features: "pois:disabled",
    supportsLanguage: true,
  },
  {
    id: "satellite",
    labelKey: "fleetLayout.mapModes.satellite",
    hintKey: "fleetLayout.mapModes.imagery",
    icon: MdSatellite,
    style: "satellite.day",
    imageFormat: "jpeg",
    supportsLanguage: false,
  },
];

function normalizeHereLanguage(languageTag) {
  const raw = String(languageTag || "").trim().replace("_", "-");
  if (!raw) return "en";

  const lowered = raw.toLowerCase();
  if (HERE_LANGUAGE_BY_LOCALE[lowered]) return HERE_LANGUAGE_BY_LOCALE[lowered];

  const baseLanguage = lowered.split("-")[0];
  if (HERE_LANGUAGE_BY_LOCALE[baseLanguage]) return HERE_LANGUAGE_BY_LOCALE[baseLanguage];

  return "en";
}

function encodeQueryValue(value) {
  return encodeURIComponent(String(value || "").trim());
}

function createBlankTileSource() {
  return new XYZ({ url: BLANK_TILE_DATA_URI, transition: 0 });
}

function normalizeAngle(value, max) {
  const modulo = value % max;
  return modulo < 0 ? modulo + max : modulo;
}

function dayOfYearUTC(date) {
  const startUtc = Date.UTC(date.getUTCFullYear(), 0, 1);
  const nowUtc = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  return Math.floor((nowUtc - startUtc) / 86400000) + 1;
}

function computeSunEventUTCHours({ date, latitude, longitude, isSunrise }) {
  const zenith = 90.833;
  const lngHour = longitude / 15;
  const n = dayOfYearUTC(date);
  const t = n + ((isSunrise ? 6 : 18) - lngHour) / 24;
  const M = 0.9856 * t - 3.289;
  const L = normalizeAngle(
    M + 1.916 * Math.sin((Math.PI / 180) * M) + 0.02 * Math.sin((Math.PI / 180) * 2 * M) + 282.634,
    360
  );

  let RA = (180 / Math.PI) * Math.atan(0.91764 * Math.tan((Math.PI / 180) * L));
  RA = normalizeAngle(RA, 360);
  const Lquadrant = Math.floor(L / 90) * 90;
  const RAquadrant = Math.floor(RA / 90) * 90;
  RA = (RA + (Lquadrant - RAquadrant)) / 15;

  const sinDec = 0.39782 * Math.sin((Math.PI / 180) * L);
  const cosDec = Math.cos(Math.asin(sinDec));
  const cosH =
    (Math.cos((Math.PI / 180) * zenith) -
      sinDec * Math.sin((Math.PI / 180) * latitude)) /
    (cosDec * Math.cos((Math.PI / 180) * latitude));

  if (cosH > 1) return { utcHours: null, alwaysNight: true, alwaysDay: false };
  if (cosH < -1) return { utcHours: null, alwaysNight: false, alwaysDay: true };

  let H = isSunrise
    ? 360 - (180 / Math.PI) * Math.acos(cosH)
    : (180 / Math.PI) * Math.acos(cosH);
  H /= 15;
  const T = H + RA - 0.06571 * t - 6.622;
  const utcHours = normalizeAngle(T - lngHour, 24);
  return { utcHours, alwaysNight: false, alwaysDay: false };
}

function isNightAtLocation({ latitude, longitude, now = new Date() }) {
  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;

  const sunrise = computeSunEventUTCHours({
    date: now,
    latitude: lat,
    longitude: lng,
    isSunrise: true,
  });
  const sunset = computeSunEventUTCHours({
    date: now,
    latitude: lat,
    longitude: lng,
    isSunrise: false,
  });

  if (sunrise.alwaysNight || sunset.alwaysNight) return true;
  if (sunrise.alwaysDay || sunset.alwaysDay) return false;
  if (!Number.isFinite(sunrise.utcHours) || !Number.isFinite(sunset.utcHours)) return false;

  const nowUtcHours =
    now.getUTCHours() +
    now.getUTCMinutes() / 60 +
    now.getUTCSeconds() / 3600;
  if (sunrise.utcHours <= sunset.utcHours) {
    return nowUtcHours < sunrise.utcHours || nowUtcHours >= sunset.utcHours;
  }

  return nowUtcHours >= sunset.utcHours && nowUtcHours < sunrise.utcHours;
}

function resolveHereStyle({ basemapConfig, mapCenter, nowMs }) {
  if (!basemapConfig.autoDayNight || !basemapConfig.baseStyle) {
    return basemapConfig.style;
  }

  const isNight = isNightAtLocation({
    latitude: mapCenter?.lat,
    longitude: mapCenter?.lng,
    now: new Date(nowMs),
  });
  return `${basemapConfig.baseStyle}.${isNight ? "night" : "day"}`;
}

function buildHereTileUrl({ apiKey, language, mapMode = "lite-auto", mapCenter, nowMs }) {
  const normalizedKey = String(apiKey || "").trim();
  const normalizedLanguage = String(language || "en").trim() || "en";
  const basemapConfig =
    HERE_BASEMAP_OPTIONS.find((option) => option.id === mapMode) || HERE_BASEMAP_OPTIONS[0];
  const style = resolveHereStyle({ basemapConfig, mapCenter, nowMs });
  const queryParts = [
    `apiKey=${encodeQueryValue(normalizedKey)}`,
    `style=${encodeQueryValue(style)}`,
    "size=256",
    "ppi=100",
  ];
  if (basemapConfig.supportsLanguage !== false) {
    queryParts.push(`lang=${encodeQueryValue(normalizedLanguage)}`);
  }
  if (basemapConfig.features) {
    queryParts.push(`features=${encodeQueryValue(basemapConfig.features)}`);
  }

  return (
    `${HERE_TILES_2D_URL_TEMPLATE.replace("/png8", `/${basemapConfig.imageFormat}`)}?` +
    queryParts.join("&")
  );
}

function createRoadTileSource({
  hereApiKey,
  language,
  mapMode = "lite-auto",
  mapCenter,
  nowMs = Date.now(),
}) {
  const apiKey = String(hereApiKey || "").trim();
  if (!apiKey) return createBlankTileSource();

  return new XYZ({
    url: buildHereTileUrl({ apiKey, language, mapMode, mapCenter, nowMs }),
    crossOrigin: "anonymous",
    attributions: HERE_ATTRIBUTION,
    maxZoom: 22,
    transition: 0,
  });
}

export default function FleetLayout() {
  const [mapCenter, setMapCenter] = useState(centerMadrid);
  const [zoomLevel, setZoomLevel] = useState(12);
  const [isMapReady, setIsMapReady] = useState(false);
  const [activeLayers, setActiveLayers] = useState([
    "availableWithOrder",
    "availableWithoutOrder",
  ]);
  const [fleetName, setFleetName] = useState();
  const [fleetLocation, setFleetLocation] = useState(null);
  const [confirmMapOpen, setConfirmMapOpen] = useState(false);
  const [rightClickData, setRightClickData] = useState(null);
  const [hoveredDriver, setHoveredDriver] = useState(null);
  const [showMoreInfoByDriver, setShowMoreInfoByDriver] = useState({});
  const [mapMode, setMapMode] = useState("lite-auto");
  const [clockTickMs, setClockTickMs] = useState(() => Date.now());

  const { fleetId, fleetData } = useContext(AuthContext);
  const { driversList, driversDetailsById, lastPosition } = useContext(DriverContext);
  const navigate = useNavigate();
  const mapHostRef = useRef(null);
  const mapRef = useRef(null);
  const mapRoadLayerRef = useRef(null);
  const mapVectorSourceRef = useRef(null);
  const mapHoverOverlayRef = useRef(null);
  const mapHoverOverlayElementRef = useRef(null);
  const mapCenterRef = useRef(centerMadrid);
  const zoomLevelRef = useRef(12);
  const hereApiKeyRef = useRef("");
  const mapTileLanguageRef = useRef("en");
  const mapModeRef = useRef("lite-auto");
  const clockTickMsRef = useRef(Date.now());
  const driversByIdRef = useRef({});
  const hoveredDriverIdRef = useRef(null);
  const onMarkerClickRef = useRef(null);

  const { t, i18n } = useTranslation();
  const primaryColor = "var(--chakra-colors-gandolapp-500)";
  const { hereApiKey } = useGlobalSettings();
  const normalizedFleetId = String(fleetId || "").trim();
  const localeTag = i18n?.resolvedLanguage || i18n?.language || "en";
  const mapTileLanguage = useMemo(() => normalizeHereLanguage(localeTag), [localeTag]);

  useEffect(() => {
    if (!fleetData) return;
    if (fleetData?.name) setFleetName(fleetData.name);
    const latitude = Number(fleetData?.location?.lat);
    const longitude = Number(fleetData?.location?.lng);
    if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
      setFleetLocation({ lat: latitude, lng: longitude });
    }
  }, [fleetData]);

  useEffect(() => {
    if (!fleetId) return () => {};
    return subscribeFleetDetails(
      fleetId,
      (nextFleetData) => {
        if (nextFleetData?.name) setFleetName(nextFleetData.name);
        const latitude = Number(nextFleetData?.location?.lat);
        const longitude = Number(nextFleetData?.location?.lng);
        if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
          setFleetLocation({ lat: latitude, lng: longitude });
        }
      },
      (error) => {
        console.error("Failed to subscribe fleet details:", error);
      }
    );
  }, [fleetId]);

  useEffect(() => {
    setMapCenter(fleetLocation ?? centerMadrid);
  }, [fleetLocation]);

  useEffect(() => {
    mapCenterRef.current = mapCenter ?? centerMadrid;
  }, [mapCenter]);

  useEffect(() => {
    zoomLevelRef.current = zoomLevel;
  }, [zoomLevel]);

  useEffect(() => {
    hereApiKeyRef.current = hereApiKey;
  }, [hereApiKey]);

  useEffect(() => {
    mapTileLanguageRef.current = mapTileLanguage;
  }, [mapTileLanguage]);

  useEffect(() => {
    mapModeRef.current = mapMode;
  }, [mapMode]);

  useEffect(() => {
    clockTickMsRef.current = clockTickMs;
  }, [clockTickMs]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setClockTickMs(Date.now());
    }, 60000);
    return () => window.clearInterval(intervalId);
  }, []);

  const mergedDrivers = useMemo(() => {
    const normalize = (value) => String(value || "").trim();

    return driversList.map((driver) => {
      const id = String(driver.id);
      const details = driversDetailsById[id] || {};
      const currentOrder = normalize(details?.currentOrder);
      const currentFleet = normalize(details?.currentFleet);
      const isInCurrentFleet = currentFleet === normalizedFleetId;
      const layerKey = !isInCurrentFleet
        ? "notAvailable"
        : currentOrder
        ? "availableWithOrder"
        : "availableWithoutOrder";

      return {
        ...driver,
        ...details,
        lastPosition: lastPosition[driver.id] ?? null,
        hasCurrentTransportOrder: !!currentOrder,
        isInCurrentFleet,
        isAvailable: isInCurrentFleet,
        layerKey,
      };
    });
  }, [driversList, driversDetailsById, lastPosition, normalizedFleetId]);

  const driversWithPosition = useMemo(
    () => mergedDrivers.filter((driver) => driver.lastPosition),
    [mergedDrivers]
  );

  const toolbarTags = useMemo(
    () => [...new Set(mergedDrivers.flatMap((driver) => driver.tags || []).filter(Boolean))],
    [mergedDrivers]
  );

  const layerCounts = useMemo(() => {
    return driversWithPosition.reduce(
      (acc, driver) => {
        acc[driver.layerKey] = (acc[driver.layerKey] || 0) + 1;
        return acc;
      },
      { notAvailable: 0, availableWithOrder: 0, availableWithoutOrder: 0 }
    );
  }, [driversWithPosition]);

  const toggleLayer = useCallback((layerKey) => {
    setActiveLayers((prevLayers) =>
      prevLayers.includes(layerKey)
        ? prevLayers.filter((activeKey) => activeKey !== layerKey)
        : [...prevLayers, layerKey]
    );
  }, []);

  const toolbarCategories = useMemo(
    () => [
      {
        key: "drivers",
        triggerLabel: t("contextToolbar.categories.drivers", { defaultValue: "Driver layers" }),
        triggerIcon: <TeamOutlined />,
        options: [
          {
            key: "availableWithOrder",
            icon: <MdLocalShipping size={16} />,
            active: activeLayers.includes("availableWithOrder"),
            count: layerCounts.availableWithOrder,
            tooltip: t("contextToolbar.options.availableWithOrder", {
              defaultValue: "Available drivers with active order",
            }),
            onToggle: () => toggleLayer("availableWithOrder"),
          },
          {
            key: "availableWithoutOrder",
            icon: <MdOutlineLocalShipping size={16} />,
            active: activeLayers.includes("availableWithoutOrder"),
            count: layerCounts.availableWithoutOrder,
            tooltip: t("contextToolbar.options.availableWithoutOrder", {
              defaultValue: "Available drivers without active order",
            }),
            onToggle: () => toggleLayer("availableWithoutOrder"),
          },
          {
            key: "notAvailable",
            icon: <MdOutlinePersonOff size={16} />,
            active: activeLayers.includes("notAvailable"),
            count: layerCounts.notAvailable,
            tooltip: t("contextToolbar.options.notAvailable", {
              defaultValue: "Drivers not working in this fleet",
            }),
            onToggle: () => toggleLayer("notAvailable"),
          },
        ],
      },
      {
        key: "messages",
        triggerLabel: t("contextToolbar.categories.messages", {
          defaultValue: "Message filters",
        }),
        triggerIcon: <MessageOutlined />,
        options: [],
        emptyText: t("contextToolbar.emptyMessagesForPage", {
          defaultValue: "No message filters on this page yet.",
        }),
      },
    ],
    [activeLayers, layerCounts, t, toggleLayer]
  );

  const handleSelectDriver = (driver) => {
    if (!driver) {
      setMapCenter(fleetLocation ?? centerMadrid);
      setZoomLevel(12);
      return;
    }

    if (driver.lastPosition) {
      setMapCenter({
        lat: driver.lastPosition.latitude,
        lng: driver.lastPosition.longitude,
      });
      setZoomLevel(18);
    }
  };

  const getArrowSymbol = (driver) => {
    const speedKmH =
      driver.lastPosition?.speed != null ? Math.round(driver.lastPosition.speed * 3.6) : 0;
    if (speedKmH < 5) return null;

    let color = "#FF0000";
    if (speedKmH > 80) color = "#006400";
    else if (speedKmH > 30) color = "#FFD700";

    return {
      color,
      rotation: ((Number(driver.lastPosition?.heading) || 0) * Math.PI) / 180,
      radius: Math.min(12, 7 + speedKmH / 18),
    };
  };

  const driverMarkerStyles = useMemo(() => createDriverMarkerStyles(), []);

  const markerStyles = useMemo(
    () => ({
      base: createFleetLocationMarkerStyle({ scale: 1 }),
    }),
    []
  );

  const mapModeClassName = useMemo(
    () => `map-mode-${String(mapMode || "lite-auto").replace(/[^a-z0-9-]/gi, "").toLowerCase()}`,
    [mapMode]
  );

  const onMarkerClick = useCallback(
    (driver) => {
      const currentZoom = mapRef.current?.getView()?.getZoom() ?? zoomLevel;
      if (currentZoom < 17.5) {
        setMapCenter({ lat: driver.lastPosition.latitude, lng: driver.lastPosition.longitude });
        setZoomLevel(18);
      } else {
        navigate("/drivers", { state: { driver } });
      }
    },
    [navigate, zoomLevel]
  );

  useEffect(() => {
    onMarkerClickRef.current = onMarkerClick;
  }, [onMarkerClick]);

  useEffect(() => {
    if (!mapHostRef.current || mapRef.current || !mapHoverOverlayElementRef.current) return;

    const roadLayer = new TileLayer({
      source: createRoadTileSource({
        hereApiKey: hereApiKeyRef.current,
        language: mapTileLanguageRef.current,
        mapMode: mapModeRef.current,
        mapCenter: mapCenterRef.current,
        nowMs: clockTickMsRef.current,
      }),
      visible: true,
    });
    const vectorSource = new VectorSource();
    const vectorLayer = new VectorLayer({
      source: vectorSource,
      declutter: true,
    });

    const map = new OlMap({
      target: mapHostRef.current,
      layers: [roadLayer, vectorLayer],
      view: new View({
        center: fromLonLat([mapCenterRef.current.lng, mapCenterRef.current.lat]),
        zoom: zoomLevelRef.current,
        minZoom: 2,
        maxZoom: 22,
      }),
    });

    const hoverOverlay = new Overlay({
      element: mapHoverOverlayElementRef.current,
      positioning: "bottom-center",
      offset: [0, -38],
      stopEvent: true,
    });
    map.addOverlay(hoverOverlay);

    const contextMenuHandler = (event) => {
      event.preventDefault();
      const coordinate = map.getEventCoordinate(event);
      const [lng, lat] = toLonLat(coordinate);
      const zoom = map.getView().getZoom();
      setRightClickData({
        lat,
        lng,
        zoom: Number.isFinite(zoom) ? Math.round(zoom) : zoomLevelRef.current,
      });
      setConfirmMapOpen(true);
    };

    const pickMainDriverFeature = (pixel) =>
      map.forEachFeatureAtPixel(
        pixel,
        (feature) => {
          if (feature.get("featureType") === "driverMain") return feature;
          return undefined;
        },
        { hitTolerance: 10 }
      );

    const pointerMoveKey = map.on("pointermove", (event) => {
      if (event.dragging) return;

      const feature = pickMainDriverFeature(event.pixel);
      const driverId = feature?.get("driverId") || null;
      const driver = driverId ? driversByIdRef.current[driverId] || null : null;

      if (driver && hoveredDriverIdRef.current !== driver.id) {
        hoveredDriverIdRef.current = driver.id;
        setHoveredDriver(driver);
      } else if (!driver && hoveredDriverIdRef.current !== null) {
        hoveredDriverIdRef.current = null;
        setHoveredDriver(null);
      }

      const targetElement = map.getTargetElement();
      if (targetElement) targetElement.style.cursor = driver ? "pointer" : "";
    });

    const singleClickKey = map.on("singleclick", (event) => {
      const feature = pickMainDriverFeature(event.pixel);
      const driverId = feature?.get("driverId") || null;
      const driver = driverId ? driversByIdRef.current[driverId] || null : null;

      if (driver) {
        onMarkerClickRef.current?.(driver);
      } else {
        hoveredDriverIdRef.current = null;
        setHoveredDriver(null);
      }
    });

    map.getViewport().addEventListener("contextmenu", contextMenuHandler);

    mapRef.current = map;
    mapRoadLayerRef.current = roadLayer;
    mapVectorSourceRef.current = vectorSource;
    mapHoverOverlayRef.current = hoverOverlay;

    const updateMapSize = () => {
      map.updateSize();
    };
    updateMapSize();
    const resizeRafId = window.requestAnimationFrame(updateMapSize);
    const resizeTimeoutId = window.setTimeout(updateMapSize, 180);

    let resizeObserver = null;
    if (typeof ResizeObserver !== "undefined" && mapHostRef.current) {
      resizeObserver = new ResizeObserver(() => {
        map.updateSize();
      });
      resizeObserver.observe(mapHostRef.current);
    }

    window.addEventListener("resize", updateMapSize);
    setIsMapReady(true);

    return () => {
      unByKey(pointerMoveKey);
      unByKey(singleClickKey);
      window.removeEventListener("resize", updateMapSize);
      window.cancelAnimationFrame(resizeRafId);
      window.clearTimeout(resizeTimeoutId);
      if (resizeObserver) resizeObserver.disconnect();
      map.getViewport().removeEventListener("contextmenu", contextMenuHandler);
      map.setTarget(undefined);
      mapRef.current = null;
      mapRoadLayerRef.current = null;
      mapVectorSourceRef.current = null;
      mapHoverOverlayRef.current = null;
      setIsMapReady(false);
    };
  }, []);

  useEffect(() => {
    if (!isMapReady || !mapRoadLayerRef.current) return;

    const roadLayer = mapRoadLayerRef.current;
    roadLayer.setSource(
      createRoadTileSource({
        hereApiKey,
        language: mapTileLanguage,
        mapMode,
        mapCenter,
        nowMs: clockTickMs,
      })
    );
    mapRef.current?.render();
  }, [hereApiKey, mapTileLanguage, mapMode, mapCenter, clockTickMs, isMapReady]);

  useEffect(() => {
    if (!hoveredDriver?.id) return;
    setShowMoreInfoByDriver((prev) => ({
      ...prev,
      [hoveredDriver.id]: false,
    }));
  }, [hoveredDriver?.id]);

  useEffect(() => {
    if (!mapRef.current || !mapCenter) return;
    mapRef.current.getView().animate({
      center: fromLonLat([mapCenter.lng, mapCenter.lat]),
      zoom: zoomLevel,
      duration: 250,
    });
  }, [mapCenter, zoomLevel]);

  useEffect(() => {
    const overlay = mapHoverOverlayRef.current;
    if (!overlay || !hoveredDriver?.lastPosition) {
      if (overlay) overlay.setPosition(undefined);
      return;
    }

    const latitude = Number(hoveredDriver.lastPosition.latitude);
    const longitude = Number(hoveredDriver.lastPosition.longitude);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      overlay.setPosition(undefined);
      return;
    }

    overlay.setPosition(fromLonLat([longitude, latitude]));
  }, [hoveredDriver]);

  useEffect(() => {
    const source = mapVectorSourceRef.current;
    if (!source) return;

    const nextFeatures = [];
    const filteredDrivers = driversWithPosition.filter((driver) =>
      activeLayers.includes(driver.layerKey)
    );
    const shouldRenderArrows = (mapRef.current?.getView()?.getZoom() ?? zoomLevel) >= 14;

    driversByIdRef.current = filteredDrivers.reduce((acc, driver) => {
      acc[driver.id] = driver;
      return acc;
    }, {});

    if (hoveredDriverIdRef.current && !driversByIdRef.current[hoveredDriverIdRef.current]) {
      hoveredDriverIdRef.current = null;
      setHoveredDriver(null);
    }

    if (fleetLocation) {
      const fleetFeature = new Feature({
        geometry: new Point(fromLonLat([fleetLocation.lng, fleetLocation.lat])),
      });
      fleetFeature.set("featureType", "base");
      fleetFeature.setStyle(markerStyles.base);
      nextFeatures.push(fleetFeature);
    }

    for (const driver of filteredDrivers) {
      const latitude = Number(driver.lastPosition?.latitude);
      const longitude = Number(driver.lastPosition?.longitude);
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) continue;

      const position = fromLonLat([longitude, latitude]);
      const arrow = getArrowSymbol(driver);

      if (arrow && shouldRenderArrows) {
        const arrowFeature = new Feature({ geometry: new Point(position) });
        arrowFeature.set("featureType", "driverArrow");
        arrowFeature.setStyle(
          new Style({
            image: new RegularShape({
              points: 3,
              radius: arrow.radius,
              rotation: arrow.rotation,
              angle: 0,
              fill: new Fill({ color: arrow.color }),
              stroke: new Stroke({ color: arrow.color, width: 1 }),
            }),
            zIndex: 10,
          })
        );
        nextFeatures.push(arrowFeature);
      }

      const driverFeature = new Feature({ geometry: new Point(position) });
      driverFeature.set("featureType", "driverMain");
      driverFeature.set("driverId", driver.id);
      driverFeature.setStyle(
        buildDriverMarkerStyleSet(driverMarkerStyles, {
          isOnline: driver.isOnline,
          hasActiveOrder: driver.hasCurrentTransportOrder,
        })
      );
      nextFeatures.push(driverFeature);
    }

    source.clear();
    source.addFeatures(nextFeatures);
  }, [activeLayers, driversWithPosition, fleetLocation, markerStyles, driverMarkerStyles, zoomLevel]);

  useEffect(() => {
    if (!hoveredDriver?.id) return;
    const refreshedDriver = driversByIdRef.current[hoveredDriver.id];
    if (refreshedDriver) setHoveredDriver(refreshedDriver);
  }, [driversWithPosition, hoveredDriver?.id]);

  const closeHoverCard = () => {
    hoveredDriverIdRef.current = null;
    setHoveredDriver(null);
  };

  const toggleDriverMoreInfo = (driverId, expanded) => {
    setShowMoreInfoByDriver((prev) => ({
      ...prev,
      [driverId]: expanded,
    }));
  };

  const hoveredDriverSpeedKmH =
    hoveredDriver?.lastPosition?.speed != null
      ? Math.round(hoveredDriver.lastPosition.speed * 3.6)
      : 0;
  const hoveredDriverTimestamp = hoveredDriver?.lastPosition?.timestamp
    ? formatTimestamp(hoveredDriver.lastPosition.timestamp)
    : null;
  const hoveredDriverExpanded = hoveredDriver?.id
    ? !!showMoreInfoByDriver[hoveredDriver.id]
    : false;

  const onOverlayCardClick = (event) => {
    event.stopPropagation();
    if (!hoveredDriver) return;
    onMarkerClick(hoveredDriver);
  };

  const onOverlayMouseLeave = () => {
    closeHoverCard();
  };

  const onOverlayToggleClick = (event) => {
    event.stopPropagation();
    if (!hoveredDriver?.id) return;
    toggleDriverMoreInfo(hoveredDriver.id, !hoveredDriverExpanded);
  };

  const onOverlayKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onOverlayCardClick(event);
    }
  };

  const openGoogleMaps = () => {
    if (!rightClickData) return;
    const { lat, lng, zoom } = rightClickData;
    const url = `https://www.google.com/maps/@${lat},${lng},${zoom}z`;
    window.open(url, "_blank");
    setConfirmMapOpen(false);
    setRightClickData(null);
  };

  return (
    <div
      className="fleet-map"
      style={{ position: "relative", width: "100%", height: "100vh" }}
      onContextMenu={(event) => event.preventDefault()}
    >
      <div className="map-toolbar-overlay">
        <ContextToolbar
          className="map-context-toolbar"
          searchSlot={
            <LayoutFilter drivers={mergedDrivers} onSelectDriver={handleSelectDriver} compact />
          }
          categories={toolbarCategories}
          actionsSlot={<BroadcastComponent tags={toolbarTags} drivers={mergedDrivers} compact />}
        />
      </div>

      <div className="map-mode-switcher" role="radiogroup" aria-label={t("fleetLayout.mapStyle")}>
        {HERE_BASEMAP_OPTIONS.map((option) => {
          const isActive = mapMode === option.id;
          const OptionIcon = option.icon;
          const optionLabel = t(option.labelKey);
          const optionHint = t(option.hintKey);
          return (
            <button
              key={option.id}
              type="button"
              role="radio"
              aria-checked={isActive}
              aria-label={`${optionLabel} ${optionHint}`}
              title={`${optionLabel} - ${optionHint}`}
              className={`map-mode-radio ${isActive ? "is-active" : ""}`}
              onClick={() => setMapMode(option.id)}
            >
              <span className="map-mode-radio-icon">
                <OptionIcon size={18} />
              </span>
            </button>
          );
        })}
      </div>

      <div
        ref={mapHostRef}
        className={`map-container_measure ${mapModeClassName}`}
        onContextMenu={(event) => event.preventDefault()}
      />

      <div
        ref={mapHoverOverlayElementRef}
        className={`fleet-map-hover-card ${hoveredDriver ? "is-visible" : ""}`}
        onMouseLeave={onOverlayMouseLeave}
        onClick={onOverlayCardClick}
        onKeyDown={onOverlayKeyDown}
        role="button"
        tabIndex={hoveredDriver ? 0 : -1}
      >
        {hoveredDriver && (
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: 6,
                fontWeight: 600,
              }}
            >
              {hoveredDriver.name || "Sin nombre"}
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                fontSize: 13,
                textAlign: "center",
                marginTop: 6,
                color: "#5f6368",
              }}
            >
              <MdUpdate size={16} color={primaryColor} style={{ marginRight: 6 }} />
              {hoveredDriverTimestamp}
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: 6,
                fontWeight: 600,
                marginTop: 6,
              }}
            >
              {hoveredDriverExpanded ? (
                <MdKeyboardArrowUp
                  size={16}
                  color={primaryColor}
                  style={{ cursor: "pointer" }}
                  onClick={onOverlayToggleClick}
                />
              ) : (
                <MdKeyboardArrowDown
                  size={16}
                  color={primaryColor}
                  style={{ cursor: "pointer" }}
                  onClick={onOverlayToggleClick}
                />
              )}
            </div>

            {hoveredDriverExpanded && (
              <div
                style={{
                  display: "flex",
                  marginTop: 12,
                  fontSize: 13,
                  paddingTop: 10,
                  borderTop: "1px solid #e0e0e0",
                  alignItems: "center",
                }}
              >
                <MdSpeed size={16} color={primaryColor} style={{ marginRight: 4 }} />
                {hoveredDriverSpeedKmH} km/h
              </div>
            )}
          </>
        )}
      </div>

      <Modal
        open={confirmMapOpen}
        onCancel={() => setConfirmMapOpen(false)}
        onOk={openGoogleMaps}
        okText={t("fleetLayout.googleMapsButton")}
        cancelText={t("fleetLayout.cancel")}
        centered
      >
        <Typography.Title level={5} style={{ marginTop: 8 }}>
          {t("fleetLayout.openGoogleMaps")}
        </Typography.Title>
        <p>{t("fleetLayout.confirmOpenGoogleMaps")}</p>
      </Modal>
    </div>
  );
}
